/**
 * WithGuideHelp Component
 *
 * A wrapper component that adds a help icon to any component.
 * By default a FILLED guide opens its full popover on HOVER (with a loader while it
 * fetches); clicking pins it open. On touch / coarse-pointer devices it falls back to
 * click-only. Set openFullOnHover={false} to keep the legacy click-only behavior.
 *
 * Usage:
 * ```tsx
 * <WithGuideHelp guideKey="strategies-description" guideService={guideService}>
 *   <Typography variant="h6">Description</Typography>
 * </WithGuideHelp>
 * ```
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { HelpTooltip } from './HelpTooltip/HelpTooltip';
import { GuideViewerPopover } from '../../guides/components/GuideViewerPopover';
import type { GuideService } from '@modules/firebase-core/services/GuideService';

/** Hover-intent timings (exported for test override). */
export const HOVER_OPEN_DELAY_MS = 250;
export const HOVER_CLOSE_DELAY_MS = 300;

export interface WithGuideHelpProps {
  /** Child component to wrap */
  children: React.ReactNode;

  /** Guide key to fetch and display */
  guideKey: string;

  /** Guide service instance */
  guideService: GuideService;

  /** Position of the help icon relative to the wrapped component */
  position?: 'top-right' | 'top-left' | 'inline-end';

  /** Size of the help icon */
  iconSize?: 'small' | 'medium' | 'large';

  /** Whether to show the lightweight preview tooltip on hover (ignored when openFullOnHover opens the full popover). */
  showPreviewOnHover?: boolean;

  /** Open the full guide popover on hover (default true; auto-disabled on touch/coarse-pointer devices). */
  openFullOnHover?: boolean;

  /** Tooltip placement for the preview */
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

  /** Custom styles for the wrapper container */
  sx?: SxProps<Theme>;

  /** Test ID for testing */
  'data-testid'?: string;

  /**
   * Accessible name (and hover-tooltip title) of the help icon button.
   * Defaults to HelpTooltip's «Get help». Pass a context-specific name when the
   * page carries several guide icons (e.g. «About this step» in the
   * create-strategy wizard).
   */
  helpLabel?: string;

  /**
   * Test ID of the help icon BUTTON itself. Without it the button falls back to
   * `${'data-testid'}-help-icon` (or the generic `help-tooltip`).
   */
  helpTestId?: string;

  /** Stops the help-icon click AND clicks inside the guide popover from bubbling into parent controls such as AccordionSummary */
  stopClickPropagation?: boolean;

  /**
   * HTML element type for the underlying help icon button. Pass `"span"` when this
   * helper is rendered inside another button (e.g. an AccordionSummary section
   * header) to avoid a `<button>` inside `<button>` DOM-nesting violation. The
   * click that opens the popover is handled by the wrapping Box, so a non-button
   * element keeps full hover/click behavior. Defaults to HelpTooltip's own button.
   */
  iconComponent?: React.ElementType;
}

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const getIconPosition = (position: 'top-right' | 'top-left' | 'inline-end') => {
  switch (position) {
    case 'top-right':
      return { position: 'absolute' as const, top: -8, right: -18 };
    case 'top-left':
      return { position: 'absolute' as const, top: -8, left: -18 };
    case 'inline-end':
      return { display: 'inline-flex', marginLeft: 1, verticalAlign: 'middle' };
    default:
      return { position: 'absolute' as const, top: -8, right: -18 };
  }
};

export const WithGuideHelp: React.FC<WithGuideHelpProps> = ({
  children,
  guideKey,
  guideService,
  position = 'top-right',
  iconSize = 'small',
  showPreviewOnHover = true,
  openFullOnHover = true,
  tooltipPlacement = 'top',
  sx,
  'data-testid': dataTestId,
  helpLabel,
  helpTestId,
  stopClickPropagation = false,
  iconComponent,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pinned, setPinned] = useState(false);
  const [openedByHover, setOpenedByHover] = useState(false);
  const [hasHover, setHasHover] = useState(false);
  const iconRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Set while a PINNED popover closes. The pinned popover is a focus-trapping
   * modal, so MUI hands focus back to the help icon as it unmounts — and that
   * focus would re-open the guide in hover mode, making the close button look
   * dead. Every close path of the modal restores focus, so the flag is always
   * consumed by the very event it was armed for.
   */
  const skipFocusOpen = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      setHasHover(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const hoverEnabled = openFullOnHover && hasHover;

  const scheduleOpen = useCallback(() => {
    if (!hoverEnabled) return;
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (anchorEl) return;
    openTimer.current = setTimeout(() => {
      if (iconRef.current) {
        setAnchorEl(iconRef.current);
        setOpenedByHover(true);
      }
    }, HOVER_OPEN_DELAY_MS);
  }, [hoverEnabled, anchorEl]);

  const scheduleClose = useCallback(() => {
    if (!hoverEnabled) return;
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    closeTimer.current = setTimeout(() => {
      setPinned(prev => {
        if (!prev) { setAnchorEl(null); setOpenedByHover(false); }
        return prev;
      });
    }, HOVER_CLOSE_DELAY_MS);
  }, [hoverEnabled]);

  // Click pins the popover open and cancels any pending hover timers (so fast clicks open instantly).
  const handleHelpClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (stopClickPropagation) event.stopPropagation();
    clearTimers();
    setAnchorEl(iconRef.current ?? event.currentTarget);
    setPinned(true);
    setOpenedByHover(false);
  }, [stopClickPropagation, clearTimers]);

  const handlePopoverClose = useCallback(() => {
    clearTimers();
    skipFocusOpen.current = pinned;
    setAnchorEl(null);
    setPinned(false);
    setOpenedByHover(false);
  }, [clearTimers, pinned]);

  // Keep the popover open while the cursor travels into it.
  const handlePopoverEnter = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);

  const handleIconFocus = useCallback(() => {
    if (!hoverEnabled) return;
    if (skipFocusOpen.current) {
      skipFocusOpen.current = false;
      return;
    }
    clearTimers();
    setAnchorEl(iconRef.current);
    setOpenedByHover(true);
  }, [hoverEnabled, clearTimers]);

  const iconPositionSx = getIconPosition(position);
  const isInline = position === 'inline-end';

  const popoverNode = (
    <GuideViewerPopover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      guideKey={guideKey}
      guideService={guideService}
      hoverMode={openedByHover}
      onMouseEnter={handlePopoverEnter}
      onMouseLeave={scheduleClose}
    />
  );

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: isInline ? 'inline-flex' : 'inline-block',
          alignItems: isInline ? 'center' : undefined,
          ...sx,
        }}
        data-testid={dataTestId}
      >
        {children}

        {/* Help Icon */}
        <Box
          ref={iconRef}
          sx={{ ...iconPositionSx, zIndex: 1 }}
          onClick={handleHelpClick}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          onFocus={hoverEnabled ? handleIconFocus : undefined}
          onBlur={hoverEnabled ? scheduleClose : undefined}
        >
          <HelpTooltip
            helpId={showPreviewOnHover && !hoverEnabled ? guideKey : ''}
            disableTooltip={hoverEnabled}
            placement={tooltipPlacement}
            size={iconSize}
            component={iconComponent}
            {...(helpLabel ? { tooltipTitle: helpLabel } : {})}
            data-testid={helpTestId ?? (dataTestId ? `${dataTestId}-help-icon` : undefined)}
            sx={{
              padding: theme.spacing(0.25),
              minWidth: 'unset',
              width: 20,
              height: 20,
              '& svg': { fontSize: 14 },
            }}
          />
        </Box>
      </Box>

      {/* Guide Viewer Popover (400×300px) */}
      {stopClickPropagation ? (
        // The popover is portalled, but React still bubbles its clicks through the component
        // tree — without this boundary "Close guide" (or any click in the guide) reaches a
        // parent AccordionSummary and toggles the section the help icon sits in.
        <Box component="span" sx={{ display: 'contents' }} onClick={stopPropagation}>
          {popoverNode}
        </Box>
      ) : popoverNode}
    </>
  );
};

WithGuideHelp.displayName = 'WithGuideHelp';
